import { createApi } from '@reduxjs/toolkit/query/react';

import { IChat } from '../../types/interfaces/chat';
import { IChatMessage } from '../../types/interfaces/chat-message';
import { CHATS, HTTP_METHOD, REDUX_CACHE } from '../constants';
import * as endpoints from '../endpoints';
import {
  TransformedErrorResponseWithNotification,
  TransformedResponseWithNotification,
  getBaseQuery,
  getListProvidesTags,
} from '../generic';

export const chatsApi = createApi({
  reducerPath: 'chatsApi',
  tagTypes: [
    CHATS.CHATS,
    CHATS.SELECTED_CHAT,
    CHATS.UNREAD_MESSAGES,
    REDUX_CACHE.ABORT_INVALIDATION,
    REDUX_CACHE.LIST,
  ],
  baseQuery: getBaseQuery(),
  // global configuration for the api
  keepUnusedDataFor: 60,
  endpoints: (builder) => ({
    getUserChatsApi: builder.query<IChat[], void>({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: endpoints.Chat.ROOT,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) => getListProvidesTags(result, CHATS.CHATS),
      ...TransformedErrorResponseWithNotification(),
    }),
    getUserUnreadMessagesApi: builder.query<IChatMessage[], void>({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: endpoints.Chat.UNREAD_MESSAGES,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) => [{ type: CHATS.UNREAD_MESSAGES }],
      ...TransformedErrorResponseWithNotification(),
    }),
    getChatByIdApi: builder.query<IChat, { chatId: string }>({
      keepUnusedDataFor: 0,
      query({ chatId }) {
        return {
          url: `${endpoints.Chat.ROOT}/${chatId}`,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) => [{ type: CHATS.SELECTED_CHAT }],
      ...TransformedErrorResponseWithNotification(),
    }),
    postPutChatApi: builder.mutation<void, { id: string; body: IChat }>({
      query({ id, body }) {
        return {
          url: endpoints.Chat.ROOT + (id ? `/${id}` : ''),
          method: id ? HTTP_METHOD.PUT : HTTP_METHOD.POST,
          body,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: CHATS.CHATS,
              id: REDUX_CACHE.LIST,
            },
            {
              type: CHATS.SELECTED_CHAT,
            },
            {
              type: CHATS.CHATS,
              id: params.id,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification('Chat'),
    }),
    sendMessageApi: builder.mutation<void, { id: string; body: IChatMessage }>({
      query({ id, body }) {
        return {
          url: endpoints.Chat.MESSAGE.replace('${0}', id),
          method: HTTP_METHOD.POST,
          body,
        };
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    chatTypingApi: builder.mutation<void, { id: string }>({
      query({ id }) {
        return {
          url: endpoints.Chat.CHAT_TYPING.replace('${0}', id),
          method: HTTP_METHOD.POST,
        };
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    readMessagesApi: builder.mutation<void, { id: string }>({
      query({ id }) {
        return {
          url: endpoints.Chat.READ_MESSAGES.replace('${0}', id),
          method: HTTP_METHOD.POST,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [{ type: CHATS.UNREAD_MESSAGES }];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    deleteChatApi: builder.mutation<void, { id: string }>({
      query({ id }) {
        return {
          url: `${endpoints.Chat.ROOT}/${id}`,
          method: HTTP_METHOD.DELETE,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: CHATS.CHATS,
              id: params.id,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification('Chat'),
    }),
  }),
});

export const resetChatsApiState = () => chatsApi.util.resetApiState();

export const {
  useGetUserChatsApiQuery,
  useLazyGetUserChatsApiQuery,
  useGetChatByIdApiQuery,
  useGetUserUnreadMessagesApiQuery,
  useLazyGetUserUnreadMessagesApiQuery,
  useLazyGetChatByIdApiQuery,
  usePostPutChatApiMutation,
  useSendMessageApiMutation,
  useReadMessagesApiMutation,
  useChatTypingApiMutation,
  useDeleteChatApiMutation,
} = chatsApi;
